import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, Typography, Paper, Grid, Card, CardContent, Button } from '@mui/material';
import { Analytics as AnalyticsIcon, Receipt, TrendingUp, Assessment } from '@mui/icons-material';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import StudentReports from './StudentReports';
import FinanceReportsPage from '../reports/FinanceReportsPage';
import ExamReportsPage from '../reports/ExamReportsPage';

const AnalyticsOverview: React.FC = () => {
  const { canRead } = useModulePermissions();

  const allModules = [
    {
      key: 'student_reports',
      title: 'Student Reports',
      description: 'Comprehensive student reports with academic and administrative data',
      icon: <Receipt sx={{ fontSize: 48, color: 'primary.main' }} />,
      path: '/app/reports/student-reports',
    },
    {
      key: 'student_reports',
      title: 'Exam Reports',
      description: 'Class-wide gradesheets ranked by percentage, and cross-exam comparisons',
      icon: <Assessment sx={{ fontSize: 48, color: 'primary.main' }} />,
      path: '/app/reports/exam-reports',
    },
    {
      key: 'finance_reports',
      title: 'Finance Reports',
      description: 'Expenses, fines, and revenue analytics with interactive charts',
      icon: <TrendingUp sx={{ fontSize: 48, color: 'success.main' }} />,
      path: '/app/reports/finance',
    },
  ];

  const availableModules = allModules.filter(m => canRead(m.key));

  if (availableModules.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Reports
        </Typography>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <AnalyticsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Report Modules Available
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You don't have permission to access any report modules. Please contact your administrator.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Simple Header */}
      <Typography variant="h4" gutterBottom>
        Reports
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
        Generate and download comprehensive student reports
      </Typography>

      {/* Simple Module Cards */}
      <Grid container spacing={2}>
        {availableModules.map((module) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={module.key}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Box sx={{ mb: 2 }}>
                  {module.icon}
                </Box>
                <Typography variant="h6" gutterBottom>
                  {module.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {module.description}
                </Typography>
                <Button
                  variant="contained"
                  href={module.path}
                  fullWidth
                >
                  Open
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Simple Features Overview */}
      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Available Features
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Comprehensive student reports with academic performance
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Administrative records and letter tracking
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • PDF report generation for offline access
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Subject-wise performance analysis
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Letter summary with fine tracking
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Professional formatted reports
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

const Analytics: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<AnalyticsOverview />} />
      <Route path="/student-reports" element={<StudentReports />} />
      <Route path="/exam-reports" element={<ExamReportsPage />} />
      <Route path="/finance" element={<FinanceReportsPage />} />
      <Route path="*" element={<Navigate to="/reports" replace />} />
    </Routes>
  );
};

export default Analytics;